import { FC } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Button, Flex, FlexProps, Icon, IconButton, useBreakpointValue } from 'src/components/ui';

interface DataTablePaginationProps {
	page: number;
	pages: number;
	nextPage: () => void;
	prevPage: () => void;
	goToPage: (pageNumber: number) => void;
	containerProps?: FlexProps;
}

const DataTablePagination: FC<DataTablePaginationProps> = ({
	page,
	pages,
	nextPage,
	prevPage,
	goToPage,
	containerProps,
}) => {
	const canGoBack = page > 1;
	const canGoNext = page < pages;
	const pagesArr = Array.from(Array(pages).keys());
	const maxVisiblePages = useBreakpointValue({ base: 3, md: 10 }) || 3;

	if (pages <= 1) return null;

	// Calculate which pages to show
	const getVisiblePages = () => {
		if (pages <= maxVisiblePages) {
			return pagesArr;
		}

		// On mobile, show current page and surrounding pages
		const half = Math.floor(maxVisiblePages / 2);
		let start = page - 1 - half;
		let end = page - 1 + half;

		// Adjust if at the beginning
		if (start < 0) {
			end += Math.abs(start);
			start = 0;
		}

		// Adjust if at the end
		if (end >= pages) {
			start -= end - pages + 1;
			end = pages - 1;
		}

		// Ensure we don't go below 0
		start = Math.max(0, start);

		return pagesArr.slice(start, end + 1);
	};

	const visiblePages = getVisiblePages();

	return (
		<Flex mt={8} justify="center" gap={2} {...containerProps}>
			<IconButton
				aria-label="Previous"
				colorScheme="gray"
				onClick={prevPage}
				variant="outline"
				isDisabled={!canGoBack}
				size={{ base: 'sm', md: 'md' }}
			>
				<Icon w={{ base: 4, md: 6 }} h={{ base: 4, md: 6 }} as={ChevronLeftIcon} />
			</IconButton>
			{visiblePages.map((num, index) => (
				<Button
					key={num}
					onClick={() => goToPage(num + 1)}
					colorScheme="gray"
					variant={page === num + 1 ? 'solid' : 'outline'}
					size={{ base: 'sm', md: 'md' }}
				>
					{num + 1}
				</Button>
			))}
			<IconButton
				aria-label="Next"
				colorScheme="gray"
				onClick={nextPage}
				variant="outline"
				isDisabled={!canGoNext}
				size={{ base: 'sm', md: 'md' }}
			>
				<Icon w={{ base: 4, md: 6 }} h={{ base: 4, md: 6 }} as={ChevronRightIcon} />
			</IconButton>
		</Flex>
	);
};

export default DataTablePagination;
